import * as R from 'ramda';
import { reduxHelper } from 'utils/reduxHelper';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';

import { Environment } from 'Environment';
import { GlobalState } from 'reactApp/core/rootReducer';
import { CurrentUserSelectors } from 'reactApp/modules/CurrentUserModule';
import * as ServiceTypes from 'reactApp/modules/ServiceModule/ServiceModule.types';
import { ServiceUtils } from 'reactApp/modules/ServiceModule/ServiceModule.utils';
import { WhitelabelConfigs } from 'reactApp/modules/WhiteLabelModule/WhiteLabelConfigs';
import { WhiteLabelTheme } from 'reactApp/modules/WhiteLabelModule/WhiteLabelModule.types';

const host = window.location.protocol + '//' + window.location.hostname; // ignore port
const privateWhiteLabel = process.env.WHITELABEL_NAME;
const privateWhiteLabelSso =
    process.env.SSO_URL ||
    host + ':7443/auth/realms/MCS/clients/front/redirect';
const privateWhiteLabelOidc =
    process.env.OIDC_URL || host + '/api/v1/auth/oidc/get_state';

// eslint-disable-next-line no-console
console.debug('label', privateWhiteLabel);

const getWhiteLabelSettings = (state: GlobalState) => {
    const project = CurrentUserSelectors.getCurrentProject(state);

    if (Environment.isPrivateBuild && privateWhiteLabel) {
        return WhitelabelConfigs[privateWhiteLabel];
    }

    if (project) {
        return project.white_label;
    }
};

const getHasWhiteLabelSettings = R.pipe(getWhiteLabelSettings, Boolean);

const getWhiteLabelSupportEmail: ReduxTypeUtils.Selector<
    string | undefined
> = R.pipe(getWhiteLabelSettings, R.path(['support_contacts', 'email']));

const getWhiteLabelSupportPhone: ReduxTypeUtils.Selector<
    string | undefined
> = R.pipe(getWhiteLabelSettings, R.path(['support_contacts', 'phone']));

const getWhiteLabelSupportLink: ReduxTypeUtils.Selector<
    string | undefined
> = R.pipe(getWhiteLabelSettings, R.path(['support_contacts', 'support_link']));

const getThemeName: ReduxTypeUtils.Selector<
    WhiteLabelTheme | undefined
> = R.pipe(getWhiteLabelSettings, R.path(['ui_settings', 'theme']));

const getPageTitle: ReduxTypeUtils.Selector<string | undefined> = R.pipe(
    getWhiteLabelSettings,
    R.path(['ui_settings', 'page_title']),
);

const getAccessConstrolEnabled = R.pipe(
    getWhiteLabelSettings,
    R.path(['ui_settings', 'hide_access_control']),
    R.not,
);

const getProjectCreationEnabled = R.pipe(
    getWhiteLabelSettings,
    R.path(['ui_settings', 'hide_project_creation']),
    R.not,
);

const getShouldDisplayExternalLinks = R.pipe(
    getWhiteLabelSettings,
    R.path(['ui_settings', 'hide_external_links']),
    R.not,
);

const getWhiteLabelStateUrl = () => privateWhiteLabelOidc;
const getHasWhiteLabelSso = () => Environment.isPrivateBuild; // сейчас все приватные сборки работают на SSO, но возможно это не навсегда
const getWhiteLabelSsoUrl = () => privateWhiteLabelSso;

const getHasAvailablePaymentMethods = R.pipe(
    getWhiteLabelSettings,
    R.path(['billing_settings', 'available_payment_methods']),
    R.isEmpty,
    R.not,
);

const getIsBillingEnabled = R.pipe(getWhiteLabelSettings, (settings) => {
    if (settings) {
        return R.path(['billing_settings', 'enabled'], settings) as boolean;
    }
    return true;
});

const getAvailableServicesApiNames: ReduxTypeUtils.Selector<
    ServiceTypes.ApiServiceName[] | undefined
> = R.pipe(getWhiteLabelSettings, R.path(['available_services']));

const getAvailableServicesInternalNames: ReduxTypeUtils.Selector<
    ServiceTypes.InternalServiceName[] | undefined
> = R.pipe(
    getAvailableServicesApiNames,
    R.ifElse(
        R.identity,
        R.map(ServiceUtils.transformApiServiceNameToInternal),
        R.identity,
    ),
);

const getIsServiceAvailable = (
    state: GlobalState,
    serviceInternalName: ServiceTypes.InternalServiceName,
) => {
    const availableServices = getAvailableServicesInternalNames(state);
    if (!availableServices) {
        return true;
    }
    return availableServices.includes(serviceInternalName);
};

const getHasServicesRestrictions = R.pipe(
    getAvailableServicesApiNames,
    Boolean,
);

const getWhiteLabelServices = R.pipe(getWhiteLabelSettings, (settings) =>
    settings ? settings.partner_services || [] : [],
);

const getTerraform = (state) => {
    return getWhiteLabelSettings(state)?.terraform;
};

const getMarketplace = (state: GlobalState) => {
    return getWhiteLabelSettings(state)?.marketplace;
};

const getDefaultCidr = (state) => getWhiteLabelSettings(state)?.cidr;

const getMainPageHeader = (state) => {
    return getWhiteLabelSettings(state)?.ui_settings?.main_page_header;
};

const getIsNetworkCreationAvailable = (state) =>
    !getWhiteLabelSettings(state)?.networks_creation_disabled;

const getIsExtIpDisabled = (state) => {
    return getWhiteLabelSettings(state)?.isExtIpDisabled;
};

export const WhiteLabelSelectors = {
    getWhiteLabelSettings,
    getMainPageHeader,
    getHasWhiteLabelSettings,
    getWhiteLabelSupportEmail,
    getWhiteLabelSupportPhone,
    getWhiteLabelSupportLink,
    getWhiteLabelServices,
    getThemeName,
    getTerraform,
    getMarketplace,
    getDefaultCidr,
    getIsNetworkCreationAvailable,
    getPageTitle,
    getHasAvailablePaymentMethods,
    getIsBillingEnabled,
    getIsServiceAvailable,
    getAccessConstrolEnabled,
    getProjectCreationEnabled,
    getShouldDisplayExternalLinks,
    getWhiteLabelStateUrl,
    getWhiteLabelSsoUrl,
    getHasWhiteLabelSso,
    getIsExtIpDisabled,
    getAvailableServicesApiNames,
    getAvailableServicesInternalNames,
    getHasServicesRestrictions,
};

export const BoundWhiteLabelSelectors = reduxHelper.bindSelectors<
    typeof WhiteLabelSelectors
>(WhiteLabelSelectors);
